import { Button, Typography } from "@suraasa/placebo-ui"
import { theme, useMediaQuery } from "@suraasa/placebo-ui-legacy"
import { QuestionStatus } from "api/resources/cpat/types"
import clsx from "clsx"
import FullPageLoading from "components/FullPageLoading"
import { Status as WebcamStatus } from "features/Cpat/Assessments/Proctoring/hooks/useCamDetection"
import { useGlobalContext } from "global/Context/context"
import { useEffect, useState } from "react"
import { useParams } from "react-router"
import routes from "routes"
import { browserIsSupportedForProctoring } from "utils/constants"
import { useCpatAssessment } from "utils/hooks/useCpatAssessment"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import UnsupportedBrowserDialog from "../Instructions/SystemRequirement/UnsupportedBrowserDialog"
import Navbar from "../Navbar"
import Proctoring from "../Proctoring"
import AssessmentFooter from "./AssessmentFooter"
import styles from "./Attempt.module.css"
import CameraPanel from "./CameraPanel"
import { ASSESSMENT_ROOT_DIV_ID } from "./constants"
import CPATWebcam from "./CpatWebcam"
import DarkOverlayLoading from "./DarkOverlayLoading"
import DurationTimer from "./DurationTimer"
import InternetConnectionBanner from "./InternetConnectionBanner"
import Question from "./Question"
import QuestionPanel from "./QuestionPanel"
import shepherdClass from "./shepherdClasses"
import SubmitConfirmationDialog from "./SubmitConfirmationDialog"
import SubmittedSuccessfullyDialog from "./SubmittedSuccessfullyDialog"
import TimeUpDialog from "./TimeUpDialog"

const AttemptCpatAssessment = () => {
  const { attemptId } = useParams()
  const { isNcte } = useGlobalContext()
  const [questionNumber, setQuestionNumber] = useState(1)

  const [webCamStatus, setWebCamStatus] = useState<
    WebcamStatus | null | undefined
  >()

  const [invalidAttemptReason, setInvalidAttemptReason] = useState("")
  const [fiveMinutesLeft, setFiveMinutesLeft] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [oneMinuteLeft, setOneMinuteLeft] = useState(false)
  const [congratulationsDialog, setCongratulationsDialog] = useState(false)

  const onTimeEnd = async () => {
    if (
      import.meta.env.VITE_CPAT_DISABLE_AUTO_SUBMIT === "true" ||
      import.meta.env.VITE_CPAT_TEST_MODE === "true"
    )
      return

    console.log("testDuration", testDuration)
    if (!testDuration) return

    const submitted = await submit({ autoSubmitReason: "time_up" })
    if (submitted) setAutoSubmitDialogOpen(true)
  }

  // Wake lock
  useEffect(() => {
    const init = async () => {
      try {
        if ("wakeLock" in navigator) {
          const wakeLockManager: any = navigator.wakeLock
          await wakeLockManager?.request("screen")
        }
      } catch (err) {
        console.log("Could not activate screen lock: " + err)
      }
    }

    init()
  }, [])

  const {
    testDuration,
    questionBank,
    markAnswer,
    submit,
    attempt,
    isSubmitting,
  } = useCpatAssessment({
    onInvalidAttempt: reason => {
      setInvalidAttemptReason(reason || "We're unable to start the assessment.")
      setTimeout(() => {
        window.location.href = routes.cpat.dashboard
      }, 5000)
    },
    attemptId: attemptId ?? "",
  })

  const unattemptedQuestionsCount = questionBank.filter(
    ({ status }) => status === QuestionStatus.UNATTEMPTED
  ).length

  const unreadQuestionsCount = questionBank.filter(
    ({ status }) => status === QuestionStatus.UNREAD
  ).length

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false)
  const [autoSubmitDialogOpen, setAutoSubmitDialogOpen] = useState(false)

  const isMdDown = useMediaQuery(theme.breakpoints.down("md"))

  const handleTimeCallback = (time: number) => {
    if (time === 300 * 1000) {
      setFiveMinutesLeft(true)
    }
    if (time === 60 * 1000) {
      setOneMinuteLeft(true)
    }
  }

  const currentQuestion = questionBank.find(q => q.sequence === questionNumber)

  useEffect(() => {
    if (currentQuestion?.status === QuestionStatus.UNREAD)
      markAnswer(currentQuestion, [])

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion])

  if (!currentQuestion || !attemptId || invalidAttemptReason)
    return <FullPageLoading message={invalidAttemptReason} />

  const handleResponseChange = (value: number) => {
    if (currentQuestion.response?.includes(value)) {
      markAnswer(currentQuestion, [])
    } else {
      markAnswer(currentQuestion, [value])
    }
  }

  const scrollIntoView = () => {
    try {
      const element = document.querySelector(".scroll-to-hook")
      if (element) {
        element.scrollIntoView({ behavior: "smooth" })
      }
    } catch (error) {
      console.error(error)
    }
  }

  const submitAssessment = async () => {
    trackingService.trackEvent(
      TrackingEvents.cpatSubmitAssessment,
      {
        total_questions: questionBank.length,
        attempted_questions: questionBank.filter(
          q => q.status === QuestionStatus.ATTEMPTED
        ).length,
        unattempted_questions: unattemptedQuestionsCount,
        unread_questions: unreadQuestionsCount,
      },
      { services: ["PostHog"] }
    )

    const submitted = await submit()

    if (submitted) {
      setCongratulationsDialog(true)
    } else {
      console.log("Failed to submit assessment")
    }
  }

  const trackQuestionChange = (
    number: number,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    mode: "previous" | "next" | "jump"
  ) => {
    const q = questionBank.find(({ sequence }) => sequence === number)
    if (!q) return
  }

  const handleQuestionChange = (number: number) => {
    trackingService.trackEvent(
      TrackingEvents.cpatSelectQuestion,
      {
        from_question: questionNumber,
        to_question: number,
        selection_method: "panel",
      },
      { services: ["PostHog"] }
    )
    scrollIntoView()
    setQuestionNumber(number)
    trackQuestionChange(number, "jump")
  }

  const onPrevious = () => {
    trackingService.trackEvent(TrackingEvents.cpatPreviousQuestion, undefined, {
      services: ["PostHog"],
    })

    if (questionNumber === 1) return
    setQuestionNumber(questionNumber - 1)
    scrollIntoView()
    trackQuestionChange(questionNumber - 1, "previous")
  }

  const onNext = () => {
    trackingService.trackEvent(TrackingEvents.cpatNextQuestion, undefined, {
      services: ["PostHog"],
    })

    if (questionNumber === questionBank.length) return
    setQuestionNumber(questionNumber + 1)
    scrollIntoView()
    trackQuestionChange(questionNumber + 1, "next")
  }

  const proctoringEnabled = attempt?.assessment.isProctored === true
  const maxWarningCount = attempt?.assessment.maxWarningCount

  /**
   * Goal is to only show the user 10 warnings but in reality we have like...40
   * We want to prevent users from actually getting auto-submitted because that is a hassle for operations
   */
  const fakeWarningCount = maxWarningCount ? 10 : null

  return (
    <DarkOverlayLoading
      enabled={isSubmitting}
      message={
        proctoringEnabled && webCamStatus === null
          ? "Checking your system"
          : isSubmitting
          ? "Submitting"
          : ""
      }
    >
      <Proctoring
        maxWarningCount={fakeWarningCount}
        disabled={!proctoringEnabled}
        attemptId={attemptId}
        setWebCamStatus={setWebCamStatus}
        assessmentId={attempt?.assessment.id || -1}
        onTotalWarningsChange={() => {}}
        render={({
          totalWarnings,
          testIsAccessible,
          videoRef,
          proctoringAlerts,
        }) => {
          return (
            <>
              {proctoringAlerts}

              {/* <PageFPS /> */}
              {browserIsSupportedForProctoring !== "supported" && (
                <UnsupportedBrowserDialog open />
              )}
              <span
                // eslint-disable-next-line tailwindcss/no-custom-classname
                className="scroll-to-hook"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />

              {congratulationsDialog && (
                <SubmittedSuccessfullyDialog
                  open={congratulationsDialog}
                  parentSelector={() =>
                    document.querySelector(`#${ASSESSMENT_ROOT_DIV_ID}`)
                  }
                />
              )}

              {submitDialogOpen && (
                <SubmitConfirmationDialog
                  markedForReviewQuestionCount={0}
                  unattemptedQuestionsCount={unattemptedQuestionsCount}
                  unreadQuestionsCount={unreadQuestionsCount}
                  open={submitDialogOpen}
                  handleClose={() => {
                    setSubmitDialogOpen(false)
                  }}
                  onSubmit={() => {
                    setSubmitDialogOpen(false)
                    submitAssessment()
                  }}
                />
              )}

              {autoSubmitDialogOpen && (
                <TimeUpDialog
                  parentSelector={() =>
                    document.querySelector(`#${ASSESSMENT_ROOT_DIV_ID}`)
                  }
                  open={autoSubmitDialogOpen}
                  handleClose={() => {
                    window.close()
                  }}
                />
              )}

              <InternetConnectionBanner />

              <div
                id={ASSESSMENT_ROOT_DIV_ID}
                // eslint-disable-next-line tailwindcss/no-custom-classname
                className={clsx(styles.mainBody, "noselect")}
              >
                <Navbar
                  navBarTitle={
                    isNcte
                      ? "National Council for Teacher Education Assessment"
                      : "Comprehensive Pedagogy Assessment of Teachers"
                  }
                  gutterBottom={false}
                  slotEnd={
                    <div className="grid grid-flow-col items-center gap-2">
                      <div className="grid items-end">
                        <div
                          className={clsx(
                            { [styles.redText]: fiveMinutesLeft },
                            styles.timerContainer
                          )}
                        >
                          {testDuration ? (
                            <DurationTimer
                              toDate={testDuration}
                              timeLeftCallback={handleTimeCallback}
                              // [1 minute, 5 minutes] (in milliseconds)
                              notifyOnTimeLeft={[60 * 1000, 300 * 1000]}
                              onEnd={onTimeEnd}
                            />
                          ) : (
                            "--:--"
                          )}
                        </div>
                        <Typography
                          variant="smallBody"
                          className="text-onSurface-500"
                        >
                          Time left
                        </Typography>
                      </div>
                      <Button
                        className={shepherdClass["submitTestButton"]}
                        size="sm"
                        color="black"
                        onClick={() => setSubmitDialogOpen(true)}
                      >
                        Submit
                      </Button>
                    </div>
                  }
                />

                <div className={clsx("h-full pt-7")}>
                  <div
                    className={clsx({
                      [styles.none]: !isMdDown || !proctoringEnabled,
                    })}
                  >
                    {fakeWarningCount !== null && (
                      <CPATWebcam videoRef={videoRef} />
                    )}
                  </div>
                  <div className="grid gap-1">
                    <div className={`${styles.content} row-span-1 grid-cols-5`}>
                      {proctoringEnabled &&
                        fakeWarningCount !== null &&
                        !isMdDown && (
                          <div
                            className={clsx("col-span-1", {
                              [styles.none]: isMdDown,
                            })}
                          >
                            <CameraPanel
                              maxWarningCount={fakeWarningCount}
                              videoRef={videoRef}
                              totalWarnings={totalWarnings}
                            />
                          </div>
                        )}
                      <div
                        className={clsx("col-span-5 ", {
                          "lg:col-span-3": proctoringEnabled,
                          "lg:col-span-4": !proctoringEnabled,
                        })}
                      >
                        <div className={clsx(styles.test, "grid")}>
                          {testIsAccessible && (
                            <Question
                              question={currentQuestion}
                              handleResponse={handleResponseChange}
                            />
                          )}
                        </div>
                      </div>

                      {!isMdDown && testIsAccessible && (
                        <div className="col-span-1">
                          <QuestionPanel
                            onQuestionChange={handleQuestionChange}
                            questions={questionBank}
                            currentQuestion={currentQuestion}
                            onClose={() => {
                              trackingService.trackEvent(
                                TrackingEvents.cpatNavigateQuestions,
                                {
                                  button_name: "toggle_questions_tray",
                                  tray_state: "close",
                                }
                              )
                            }}
                          />
                        </div>
                      )}
                    </div>
                    {testIsAccessible && (
                      <div
                        className={clsx(
                          "grid grid-cols-5 gap-7",
                          styles.footerContainer
                        )}
                      >
                        <div
                          className={clsx("col-span-5", {
                            "lg:col-span-3 lg:col-start-2 lg:pl-1":
                              proctoringEnabled,
                            "lg:col-span-4 lg:pl-3": !proctoringEnabled,
                          })}
                        >
                          <AssessmentFooter
                            onQuestionChange={handleQuestionChange}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            questions={questionBank}
                            currentQuestion={currentQuestion}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )
        }}
      />
    </DarkOverlayLoading>
  )
}

export default AttemptCpatAssessment
